import {helpers, maxLength, minLength, required} from '@vuelidate/validators';
import {computed, ref, unref, watch} from 'vue';
import type {Ref} from 'vue';
import type {ActionCommand, ActionDecorator} from '~/lib/types/actionTypes';
import type {FormElementItem} from '~/lib/types/formTypes';
import {getActionRepeatText} from '~/lib/actionHelper';
import type {ResponsibleDecorator} from '~/lib/types/responsibleTypes';
import type {AttachmentResource} from '~/lib/types/uploads';
import {sortCaseInsensitive} from '~/composables/useSorting';
import AddResponsibleFormElement from '~/components/actions/AddResponsibleFormElement.vue';
import type {Risk} from '~/lib/types/riskTypes';
import {useUploadInput} from '~/composables/uploads/useUpload';

export const actionCommandFormLabels = {
	description: 'Actieomschrijving',
	startDate: 'Begindatum',
	endDate: 'Einddatum',
	repeat: 'Herhaling',
	remark: 'Aanvullende toelichting',
	attachments: 'Bijlagen',
	targetingMeasureKey: 'Kies een aanbevolen maatregel'
} satisfies Partial<Record<keyof ActionCommand, string>>;

const equalOrBeforeEndDate = (value: string, siblings: { endDate: string }) => {
	const endDate = siblings.endDate;
	if (!helpers.req(value) || !endDate) {
		return true;
	}
	return new Date(value) <= new Date(endDate);
};

/**
 * The responsible is required. By default, the value is NaN.
 */
const requiredResponsible = (value: number) => {
	return helpers.req(value) && !isNaN(value);
};

const requiredSuggestedMeasure = (value: string, siblings: { description: string }) => {
	if (siblings.description.length > 0) {
		return true;
	}

	/**
	 * value === '' is the option 'Describe your own measure'.
	 * Description field is required, so we can pass this field
	 */
	if (value === '') {
		return true;
	}

	return helpers.req(value);
};

const requiredDescription = (value: string, siblings: { targetingMeasureKey: string }) => {
	return siblings.targetingMeasureKey || helpers.req(value);
};

const endDateIsMoreThenOneYearInTheFuture = (value: string, siblings: { endDate: string }) => {
	const endDate = siblings.endDate;
	if (helpers.req(value) || !endDate) {
		return true;
	}

	return new Date(endDate) <= new Date(new Date().setFullYear(new Date().getFullYear() + 1));
};

type RiskForAction = {
	riskId: number,
	suggestedMeasures?: Risk['suggestedMeasures'],
}

export default (risk: RiskForAction, responsibles: Ref<Array<ResponsibleDecorator>>, initialValue?: ActionDecorator) => {
	const responsibleOptions = computed<Array<{ label: string, value: string }>>(() => {
		return responsibles.value
			.toSorted((responsible1, responsible2) => {
				return sortCaseInsensitive(responsible1.fullName, responsible2.fullName, 'asc');
			})
			.map((responsible) => {
				return {label: responsible.fullName, value: String(responsible.id)};
			});
	});

	const repeatOptions: Array<{ label: string, value: string }> = [
		{
			value: 'NON_REPEATING',
			label: getActionRepeatText('NON_REPEATING')
		},
		{
			value: 'MONTHLY',
			label: getActionRepeatText('MONTHLY')
		},
		{
			value: 'QUARTERLY',
			label: getActionRepeatText('QUARTERLY')
		},
		{
			value: 'YEARLY',
			label: getActionRepeatText('YEARLY')
		}
	];

	const getDescriptionByMeasureKey = (action?: ActionDecorator): Partial<ActionCommand> => {
		if (!action?.command?.targetingMeasureKey) {
			return {};
		}

		return {
			description: action.description?.text ?? action.command.description ?? ''
		};
	};

	const getFormFields = (): ActionCommand => {
		if (!initialValue?.command) {
			return {
				description: '',
				responsibleId: NaN,
				startDate: '',
				endDate: '',
				remark: '',
				repeat: 'NON_REPEATING',
				newAttachments: [],
				attachments: [],
				riskId: risk.riskId,
				targetingMeasureKey: '',
				reasonForDelay: ''
			} satisfies ActionCommand;
		}
		return {
			...initialValue.command,
			...getDescriptionByMeasureKey(initialValue)
		};
	};

	const formFields = ref<ActionCommand>({
		...getFormFields()
	});

	watch(
		responsibles, (newValue, oldValue) => {
			// If a responsible is added, select it by default
			if (oldValue && oldValue.length !== 0 && newValue.length > oldValue.length) {
				const newResponsible = newValue[newValue.length - 1];

				if (newResponsible.active) {
					formFields.value.responsibleId = newResponsible.id;
				}

				return;
			}

			// If there is only one responsible, and there was no value selected yet, select it by default
			if (newValue.length === 1 && !formFields.value.responsibleId) {
				formFields.value.responsibleId = newValue[0].id;
			}
		},
		{
			immediate: true
		}
	);

	const actionDescription = computed(() => formFields.value.description);
	const targetingMeasureKey = computed(() => formFields.value.targetingMeasureKey);

	watch(actionDescription, (newValue) => {
		if (newValue && newValue.length > 0 && formFields.value.targetingMeasureKey) {
			formFields.value.targetingMeasureKey = '';
		}
	});
	watch(targetingMeasureKey, (newValue) => {
		if (newValue && newValue.length > 0) {
			formFields.value.description = '';
		}
	});

	const getMeasuresOptions = () => {
		if (!risk.suggestedMeasures || risk.suggestedMeasures.length === 0) {
			return [];
		}
		return ref([
			...risk.suggestedMeasures.map((suggestedMeasure) => {
				return {
					label: suggestedMeasure.content?.text ?? '',
					value: suggestedMeasure.key ?? ''
				};
			}),
			{
				label: 'Zelf actie omschrijven',
				value: ''
			}
		]);
	};

	const selectedMeasure = computed(() => {
		return formFields.value.targetingMeasureKey;
	});

	const hasSelectedMeasure = computed(() => selectedMeasure.value !== undefined && !!selectedMeasure.value);

	const descriptionAttributes = computed(() => {
		if (hasSelectedMeasure.value) {
			return {
				placeholder: risk?.suggestedMeasures?.find(measure => measure.key === unref(selectedMeasure))?.content?.text ?? '',
				readonly: 'readonly'
			};
		}
		return {
			placeholder: 'Omschrijf de actie'
		};
	});

	const reasonForDelayAttributes = computed(() => {
		if (!endDateIsMoreThenOneYearInTheFuture('', {endDate: formFields.value.endDate})) {
			return {};
		}
		return {
			readonly: 'readonly'
		};
	});

	const descriptionTitle = computed(() => {
		if (hasSelectedMeasure.value) {
			return `${actionCommandFormLabels.description} (Geadviseerde maatregel)`;
		}
		return actionCommandFormLabels.description;
	});

	const titleFields = (): Array<FormElementItem<ActionCommand>> => {
		const showMeasuresFields = (!initialValue && risk.suggestedMeasures && risk.suggestedMeasures.length > 0) ?? false;
		const fields: Array<FormElementItem<ActionCommand>> = [];

		if (showMeasuresFields) {
			fields.push({
				label: actionCommandFormLabels.targetingMeasureKey,
				key: 'targetingMeasureKey',
				type: 'radio',
				validators: {
					requiredSuggestedMeasure: helpers.withMessage('Dit veld is verplicht', requiredSuggestedMeasure)
				},
				options: getMeasuresOptions(),
				attrs: {
					'radioStyle': 'block-items'
				}
			});
		}

		fields.push({
			label: descriptionTitle.value,
			key: 'description',
			type: 'textarea',
			validators: {
				requiredDescription: helpers.withMessage('Dit veld is verplicht', requiredDescription),
				minLength: minLength(2),
				maxLength: maxLength(1000)
			},
			attrs: descriptionAttributes
		});

		return fields;
	};

	const {uploadInputAttrs} = useUploadInput(formFields);

	const formSchema: Array<FormElementItem<ActionCommand>> = [
		...titleFields(),
		{
			label: 'Kies de verantwoordelijke',
			key: 'responsibleId',
			type: 'select',
			validators: {
				requiredResponsible: {
					$validator: requiredResponsible
				}
			},
			options: responsibleOptions
		},
		{
			label: 'Nieuwe verantwoordelijke toevoegen',
			key: 'add-responsible',
			type: 'component',
			validators: {},
			attrs: {
				is: 'AddResponsibleTest'
			},
			component: AddResponsibleFormElement
		},
		{
			label: 'Kies de periode',
			key: 'datesGroup',
			type: 'group',
			validators: {},
			fields: [
				{
					label: actionCommandFormLabels.startDate,
					key: 'startDate',
					type: 'date',
					validators: {
						beforeEndDate: helpers.withMessage('De begindatum moet voor de einddatum liggen', equalOrBeforeEndDate)
					}
				},
				{
					label: actionCommandFormLabels.endDate,
					key: 'endDate',
					type: 'date',
					validators: {
						required
					}
				},
				{
					label: actionCommandFormLabels.repeat,
					key: 'repeat',
					type: 'select',
					validators: {
						required
					},
					options: repeatOptions
				}
			]
		},
		{
			label: 'Reden waarom er meer dan een jaar tijd mag worden genomen om deze actie af te ronden',
			key: 'reasonForDelay',
			type: 'textarea',
			validators: {
				maxLength: maxLength(1000),
				requiredIf: helpers.withMessage(
					'Dit veld is verplicht omdat de einddatum meer dan een jaar in toekomst ligt',
					endDateIsMoreThenOneYearInTheFuture
				)
			},
			attrs: reasonForDelayAttributes
		},
		{
			label: actionCommandFormLabels.remark,
			key: 'remark',
			type: 'textarea',
			validators: {
				maxLength: maxLength(1000)
			}
		},
		{
			label: actionCommandFormLabels.attachments,
			key: 'attachments',
			type: 'upload',
			validators: {},
			getValue: (item) => item.attachments.map((attachment: AttachmentResource) => attachment.upload),
			attrs: uploadInputAttrs()
		}
	];

	const isReasonForDelayReadOnly = computed(() => {
		return reasonForDelayAttributes.value.readonly === 'readonly';
	});

	watch(isReasonForDelayReadOnly, () => {
		formFields.value.reasonForDelay = '';
	});

	return {
		formSchema,
		formFields
	};
};
